// screens/Clark/answerApi.js — CLARK B3 · STEP 3. The research turn, client side.
//
// 🔴 THERE IS NO CLIENT FLAG, DELIBERATELY. Retrieval is gated by CLARK_RETRIEVAL on the SERVER
// (`=== '1'`, default OFF). Adding a `VITE_CLARK_RETRIEVAL` beside it would create two switches
// that must agree, and the failure mode of two flags is that one of them is wrong and nobody can
// tell which. The server answers 200 `{ enabled: false }` when it is off; this module remembers
// that for the page's lifetime so the extra round trip is paid once, not once per question.
//
// 🔴 BUNDLE 2 · STEP 1 — THE PRE-CHECK HAS MOVED, AND ITS BIAS HAS BEEN INVERTED.
//
// It lived here as `looksLikeResearch`, a nine-template whitelist "BIASED TOWARD *NOT* ROUTING" on
// the reasoning that a research question sent to the citation path "costs one ordinary Clark turn
// and the user rephrases." That reasoning was wrong and this is where it cost us: asked "What is
// the status of abortion law in the US?", the whitelist missed, the turn fell through to the
// persona, and Clark refused a question Research answered on Dobbs three feet away.
//
// It now lives in `./researchRoute.mjs` — `.mjs` because this file is ESM inside a
// `"type":"commonjs"` package, which node cannot import, which is why the single most consequential
// branch in Clark had no test. Re-exported here so every existing import keeps working.

// 🔴 A2 — EVERY TERMINAL FAILURE NOW CARRIES A CLASSIFIED `failure`, FROM THE SHARED VOCABULARY.
// Before this, a 401 fell through to the generic "The research engine couldn't answer that one."
// A signed-out user was told the engine had a problem — so they retried, and retried, and the one
// fact that would have fixed it in five seconds was never on screen. `classifyFailure` tests
// sign-in FIRST for exactly that reason. See packages/ui/failure.mjs.
import { classifyFailure, FAILURES } from "@citeclerk/ui/failure";
import { readMeterBlock } from "@citeclerk/ui/meterBlock";

// Remembered across questions, reset only by a reload. `null` = not yet known.
let _retrievalEnabled = null;
let _disabledMessage = null;

/** For tests and for the diagnostic route. */
export function _resetRetrievalMemo() { _retrievalEnabled = null; _disabledMessage = null; }
export function retrievalKnownDisabled() { return _retrievalEnabled === false; }
export function retrievalDisabledMessage() { return _disabledMessage; }

export { looksLikeResearch, classifyClarkTurn, isOverride, ADVICE_NOTICE } from "./researchRoute.mjs";

/**
 * Ask for a research answer.
 * @returns {Promise<{status:'answer'|'decline'|'disabled'|'error'|'capped', data?:object, message?:string}>}
 *
 * Never throws, and never returns a shape the caller has to guess about — the four statuses map
 * one-to-one onto four different things the surface says.
 */
export async function fetchResearchAnswer(authedFetch, { q, scope = "all", chatId = null }) {
  if (_retrievalEnabled === false) {
    return { status: "disabled", message: _disabledMessage };
  }
  let res;
  try {
    res = await authedFetch("/api/clark/answer", {
      method: "POST",
      body: JSON.stringify({ q, scope, chatId }),
    });
  } catch (e) {
    const failure = FAILURES.NETWORK;
    return { status: "error", failure, message: failure.message };
  }

  const data = await res.json().catch(() => ({}));

  /* RUN GC1 — the same `reply`-vs-`message` shape bug as the stream path below, in the route that
     predates it. Both are fixed, because a caller that branches on `status: "capped"` must be able
     to trust that `message` is a sentence whichever door the refusal came through. */
  const nsBlock = readMeterBlock(data);
  if (nsBlock || (res.status === 429 && data?.capped)) {
    return { status: "capped", block: nsBlock,
      message: data?.reply || data?.message || nsBlock?.serverMessage || FAILURES.QUOTA.message,
      data, failure: FAILURES.QUOTA };
  }
  if (data?.enabled === false) {
    _retrievalEnabled = false;
    _disabledMessage = data.message || FAILURES.RETRIEVAL_DISABLED.message;
    return { status: "disabled", failure: FAILURES.RETRIEVAL_DISABLED, message: _disabledMessage };
  }
  _retrievalEnabled = true;

  if (!res.ok || data?.ok === false) {
    const failure = classifyFailure({ status: res.status, reason: data?.reason, policy: data?.error });
    return { status: "error", failure, message: failure.message };
  }
  // 🔴 A GATE DECLINE IS AN ANSWER, NOT AN ERROR. Zero verified sentences means the deterministic
  // gate dropped everything, which is the product working — and it must render as the designed
  // refusal, never as silence and never as a failure the user reads as our outage.
  if (!data.sentences?.length) {
    return { status: "decline", data };
  }
  return { status: "answer", data };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴🔴 RUN AN — HOW MUCH IS ON SCREEN, ON **EITHER** ANSWER SHAPE.
 *
 * The three error returns below all reported `painted: live.sentences.length`, and `live` is
 * REPLACED by the v2 envelope the moment an `answer-v2` event arrives (see the note on `let live`).
 * **A v2 envelope has no `sentences` key, by construction** — clarkAnswerV2.js says so in capitals.
 * So on any v2 turn that ended in an error, this threw:
 *
 *     Cannot read properties of undefined (reading 'length')
 *         at streamResearchAnswer (screens/Clark/answerApi.js:296)
 *         at async screens/Clark/index.jsx:604
 *
 * 🔴 AND THE THROW IS WORSE THAN THE WRONG NUMBER WOULD HAVE BEEN. It escapes before ANY of the
 * caller's terminal branches run — including the one whose comment reads *"EVERY OTHER OUTCOME MUST
 * REMOVE THE PLACEHOLDER. Leaving it would strand a row that says 'Searching the corpus' forever —
 * the hang this bundle exists to make impossible."* The hang that bundle made impossible came back,
 * through a field v2 deleted.
 *
 * DRIVEN, not reasoned: with `STEEL_ANSWER_MAX_TOKENS=64` forcing a truncation on every turn, the
 * browser shows the user's question, the answer chrome, a "Copy Answer" button over an EMPTY answer,
 * and three animated dots that never stop. In normal operation the same state was reached on
 * **4 of 12 cold Clark questions** (`ops/run-aj-clark-research/out/an-clark-cold.json`) because the
 * answer model was being truncated at `max_tokens: 2048`.
 *
 * ⭐ THE PATTERN, WHICH THIS REPO HAS NOW PAID FOR THREE TIMES: **a read written against the
 * pre-v2 shape becomes a live failure when the v2 shape arrives.** `putCached`'s `sentences` guard
 * silently refused every v2 row for a release; `getCached`'s twin guard hid it; this one crashes.
 * The fix is the same each time — ask what the field MEANT, and answer that question on both shapes.
 * Here it meant "is there anything on screen worth keeping", and for v2 that is the prose.
 */
export function paintedCount(live) {
  if (Array.isArray(live?.sentences)) return live.sentences.length;      // pre-v2 / streamed
  if (typeof live?.prose === 'string' && live.prose.length) return 1;     // v2: prose is the unit
  return 0;
}

/**
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * CLARK B5 · STEP 2 — the streaming research turn.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Same four terminal statuses as `fetchResearchAnswer`, so the caller's branching is unchanged.
 * The difference is `onProgress(patch)`, called as events arrive with a partial answer object the
 * surface can render immediately.
 *
 * 🔴 fetch + a ReadableStream reader, NOT EventSource. EventSource cannot POST, cannot send the
 * Clerk auth header `authedFetch` attaches, and cannot carry a body. It would also auto-reconnect
 * on close — which for a paid research turn means silently re-running it.
 *
 * 🔴 THE SERVER MAY DECLINE TO STREAM AT ALL. The flag-off, unconfigured, limitation and capped
 * replies come back as ordinary JSON with a JSON content-type. That is checked BEFORE reading a
 * stream, because parsing a refusal as an empty stream would render a decline nobody issued.
 *
 * 🔴 IT NEVER LEAVES THE TURN SPINNING. Every exit is a resolved status; a stream that ends
 * without `done` is an error, not an empty answer.
 */
export async function streamResearchAnswer(authedFetch, { q, scope = "all", chatId = null }, onProgress = () => {}, bounds = {}) {
  if (_retrievalEnabled === false) {
    return { status: "disabled", message: _disabledMessage };
  }

  // 🔴 CLARK-RECON · LANE 7 · DEFECT 2 — THIS FILE HAD NO AbortSignal ANYWHERE.
  //
  // Nothing bounded the stream. A server that stops sending frames but holds the socket open left
  // the reader loop awaiting forever: the turn stayed `streaming: true`, `setSending(false)` was
  // never reached, and the turn had NO terminal state at all. A pending state that never terminates
  // is indistinguishable from a broken one, which is exactly why this went unnoticed.
  //
  // TWO bounds, because they fail differently and one does not cover the other:
  //
  //   TOTAL_MS = 150_000 — the whole-turn ceiling. It is deliberately LONGER than the server's own
  //   engine timeout (`STEEL_ENGINE_TIMEOUT_MS=120000`, read from the live machine) plus transport
  //   margin, so that when the server is merely slow IT wins the race and emits its `error` frame —
  //   a terminal state WITH WORDS, which is strictly better than a client-side abort with generic
  //   ones. This bound exists for the case where the server never speaks again at all.
  //
  //   IDLE_MS = 45_000 — the stall bound, reset on every frame. The failure mode above is a socket
  //   held open in silence, and a total-duration bound would make the user wait the full 150s for
  //   it. 45s is ~2.5x the largest inter-frame gap this route is known to produce: B7 audited it
  //   surviving 16.7s and 18.0s gaps, and this bundle's own Lane 1 cold rows ran 8.4s-18.6s
  //   end-to-end. Anything tighter would abort a healthy slow answer, which is the worse error.
  //
  // Both abort the SAME controller, so there is one cancellation path and one catch below.
  // The seam exists so the acceptance test can drive the REAL abort path in milliseconds. A test
  // that must wait 45 seconds does not get run, and a test nobody runs is worse than no test — this
  // whole defect shipped past a test that only grepped the file. Production passes nothing and gets
  // the stated numbers.
  /* ══ 🔴🔴 RUN PY · STEP B — THE STALL BOUND COULD NEVER FIRE, AND THE HEARTBEAT IS WHY. ═══════
   *
   * RUN NA drove *"Summarize the opinion in Kennedy v. Bremerton School District"* against
   * production and got this:
   *
   *     classified 221ms · check 221ms · stage 258ms
   *     heartbeat 10246ms → heartbeat 140277ms   (14 of them, ~10s apart)
   *     [nothing else, ever]  → 150,001ms, aborted by TOTAL
   *
   * `IDLE_MS` was reset by BYTES ARRIVING, and **a heartbeat is bytes**. Steel emits one every
   * 10 s from inside the await (its own route says so, deliberately, and correctly). So a 45 s
   * stall bound could not fire against a server that was silent-but-pinging — not on this turn,
   * not on any turn, ever. The user waited 150 s for a generic client-side abort.
   *
   * ⭐ AND THE ONE-LINE FIX — "reset the idle bound on meaningful frames only" — WOULD HAVE
   * RESURRECTED THE DEFECT THE HEARTBEAT EXISTS TO PREVENT. `clarkAnswer.js`'s RUN AN block
   * measured this route emitting NOTHING between `stage(answer)` and `stage(resolve)` for the
   * whole model call: twelve cold questions at p50 30,329 ms / p90 40,562 ms / max 42,458 ms, and
   * one at 46,893 ms that the browser threw away at 45,000 ms — *"a complete answer, computed,
   * resolved and paid for"*. Moving the 45 s bound onto meaningful frames re-creates that
   * verbatim. The gap is not a stall; it is the work.
   *
   * So there are THREE bounds, because there are three different questions:
   *
   *   IDLE_MS     45,000   IS THE SOCKET ALIVE?   reset by BYTES — a heartbeat legitimately
   *                        answers this one, and at a 10 s interval 45 s of silence means the
   *                        transport is genuinely gone. Unchanged, and now correctly scoped.
   *   PROGRESS_MS 120,000  IS THE WORK ADVANCING? reset ONLY by a MEANINGFUL frame. A heartbeat
   *                        carries no claim about the work (steel's own words), so it must not be
   *                        able to satisfy a bound that asks about the work. 120 s is ~2.6x the
   *                        largest meaningful-frame gap ever measured on this route (46.9 s) and
   *                        sits ABOVE the server's own 100 s turn deadline, so the server still
   *                        wins the race and still speaks first — see below.
   *   TOTAL_MS    150,000  the whole-turn ceiling. Unchanged.
   *
   * 🔴 AND THE PREMISE OF THE OLD COMMENT WAS FALSE. It said TOTAL_MS is *"deliberately LONGER
   * than the server's own engine timeout (STEEL_ENGINE_TIMEOUT_MS=120000) … so that when the
   * server is merely slow IT wins the race and emits its error frame — a terminal state WITH
   * WORDS."* There was no such race. `STEEL_ENGINE_TIMEOUT_MS` is read in exactly one place —
   * `apps/steel/server/lib/engine.js` — and bounds only POSTs to the PYTHON engine. The v2 answer
   * stage never calls it and passes no AbortSignal to the model at all, so nothing on the server
   * was ever going to fire. RUN PY gives the server the clock the comment assumed it had
   * (`STEEL_CLARK_TURN_DEADLINE_MS`, default 100 s, in `routes/clarkAnswer.js`); the bounds here
   * are the backstop for when even that cannot speak. */
  const TOTAL_MS = bounds.totalMs ?? 150_000;
  const IDLE_MS = bounds.idleMs ?? 45_000;
  const PROGRESS_MS = bounds.progressMs ?? 120_000;
  const ctl = new AbortController();
  let abortReason = null;
  const abortWith = (reason) => { abortReason = reason; try { ctl.abort(); } catch { /* already aborted */ } };
  const totalTimer = setTimeout(() => abortWith("total"), TOTAL_MS);
  let idleTimer = setTimeout(() => abortWith("idle"), IDLE_MS);
  let progressTimer = setTimeout(() => abortWith("progress"), PROGRESS_MS);
  // reset on every chunk of BYTES — declared here so the read loop below can call it.
  const beat = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => abortWith("idle"), IDLE_MS);
  };
  /* Reset ONLY by a frame that says something about the work. The list is the frames this route
     actually emits minus `heartbeat`, and it is a DENY of one rather than an ALLOW of many on
     purpose: a frame added tomorrow is progress by default, and the one frame that is not is the
     one this whole block is about. */
  const progressed = (frameName) => {
    if (frameName === "heartbeat") return;
    clearTimeout(progressTimer);
    progressTimer = setTimeout(() => abortWith("progress"), PROGRESS_MS);
  };
  const clearTimers = () => { clearTimeout(totalTimer); clearTimeout(idleTimer); clearTimeout(progressTimer); };

  let res;
  try {
    res = await authedFetch("/api/clark/answer/stream", {
      method: "POST",
      headers: { accept: "text/event-stream" },
      // A8 — the research route persists too now, and it needs to be told WHICH chat.
      body: JSON.stringify({ q, scope, chatId }),
      signal: ctl.signal,
    });
  } catch {
    clearTimers();
    // The request never reached a server — the audit's repro (abort at the network layer) lands
    // exactly here, and this is the return that used to fall through to the persona.
    const failure = FAILURES.NETWORK;
    return { status: "error", failure, message: failure.message };
  }

  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("text/event-stream")) {
    // Not a stream: nothing left to time out. Cancel both bounds before ANY of the returns below,
    // so a JSON refusal cannot leave a 150s timer holding this closure alive.
    clearTimers();
    // A refusal, a limitation, or a cap — all JSON, all handled exactly as the non-streaming path
    // handles them, so there is one set of rules about what these replies mean.
    const data = await res.json().catch(() => ({}));
    /* ══════════════════════════════════════════════════════════════════════════════════════════
       🔴🔴 RUN GC1 — `data.reply` IS A KEY THE FREE-TIER REFUSAL DOES NOT HAVE, AND THAT ONE
       MISMATCH IS THE WHOLE OF THE FOUNDER'S CLARK DEFECT.

       Both 429s reach this line as `{enabled:true, capped:true, …}`:
         · the DAILY cap (clarkMeter.clarkRefusal) carries `reply` — a full sentence;
         · the PLAN quota (meter.cjs blockBody, the FREE-TIER case) carries `message`, `meter`,
           `plan`, `limit` — and no `reply` at all.
       So `message: data.reply` was `undefined` for exactly the case a free user hits, the caller
       appended an assistant turn with `text: undefined`, and Clark's byline, orb and timestamp
       painted above an EMPTY BUBBLE. Measured in a browser at HEAD (ops/run-gc1/step0-clark.mjs):
       the daily-cap arm rendered its sentence correctly and the plan arm rendered nothing, from the
       same 429, in the same component — which is what makes it a shape bug and not a copy bug.

       `block` is the server's own refusal, normalised. A caller that has it renders a modal; a
       caller that has not been taught about it still gets a TRUE sentence, because `message` now
       falls back through `reply` → `message` → the classified line, and never to `undefined`. */
    const block = readMeterBlock(data);
    if (block || (res.status === 429 && data?.capped)) {
      return { status: "capped", block,
        message: data?.reply || data?.message || block?.serverMessage || FAILURES.QUOTA.message,
        data, failure: FAILURES.QUOTA };
    }
    // 🔴 A LIMITATION IS AN ANSWERED DECLINE (the gate spoke), NOT A FAILURE. Checked before the
    // classifier so a designed refusal is never relabelled as an outage.
    if (data?.limitation) { _retrievalEnabled = true; return { status: "decline", data }; }
    const failure = classifyFailure({
      status: res.status, reason: data?.reason, enabled: data?.enabled, capped: data?.capped, policy: data?.error,
    });
    if (failure.kind === "retrieval-disabled") {
      _retrievalEnabled = false;
      _disabledMessage = data.message || failure.message;
      return { status: "disabled", failure, message: _disabledMessage };
    }
    return { status: "error", failure, message: failure.message };
  }
  _retrievalEnabled = true;

  if (!res.body) {
    clearTimers();
    const failure = FAILURES.RETRIEVAL_FAILED;
    return { status: "error", failure, message: failure.message };
  }

  // Progressive state. Kept here rather than in the component so a re-render cannot lose a
  // sentence that arrived between two renders.
  // 🔴 `let`, NOT `const` — the v2 branch below REPLACES this object rather than mutating it, and
  // it has to: a v2 envelope must never end up carrying the `sentences` key this seed creates.
  // (It was `const`, and the reassignment would have thrown a TypeError on the first v2 turn. Same
  // class as `streamingTurnId` shipping unbound — caught here only because the flag was exercised.)
  let live = { q, scope, streaming: true, sentences: [], authorities: [], statutes: [], checks: [] };
  const upsertCheck = (c) => {
    const i = live.checks.findIndex((x) => x.id === c.id);
    if (i >= 0) live.checks[i] = c; else live.checks.push(c);
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done = null;
  let errored = null;

  try {
    for (;;) {
      const { value, done: eof } = await reader.read();
      if (eof) break;
      // The TRANSPORT bound is reset by BYTES ARRIVING, not by frames parsed: a server dribbling
      // a partial frame is alive, and aborting it would be the false positive that makes a
      // timeout worse than no timeout. The PROGRESS bound is a different question and is reset
      // per-frame below, by `progressed()`.
      beat();
      buf += decoder.decode(value, { stream: true });
      let cut;
      // Frames split on a blank line. Consume only COMPLETE frames — a frame straddling a chunk
      // boundary is the normal case on a slow answer, not the edge case.
      while ((cut = buf.indexOf("\n\n")) !== -1) {
        const frame = buf.slice(0, cut);
        buf = buf.slice(cut + 2);
        let name = "message";
        const dataLines = [];
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) name = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
        }
        if (!dataLines.length) continue;
        let payload;
        try { payload = JSON.parse(dataLines.join("\n")); } catch { continue; }

        if (name === "classified") {
          live.template = payload.template; live.variant = payload.variant; live.classifier = payload.classifier;
        } else if (name === "check") {
          upsertCheck({ id: payload.id, label: payload.label, status: payload.status, detail: payload.detail });
        } else if (name === "stage") {
          /* 🔴 RUN AP · PART 2 — WHAT THE SERVER IS DOING RIGHT NOW, AND IT IS NOT A `check`.
             A check is a VERDICT on the finished answer and is rendered as a trace row with a
             tick. A stage is a statement about work in flight with no claim about its outcome, so
             it is held on its own key and rendered beside the dots. Collapsing the two would put
             a green tick next to work that has not finished — a progress display that begins
             lying without anyone deciding it should.
             LAST ONE WINS, deliberately: this is a current-activity label, not a list. The
             pipeline emits four in order and only the newest is true. */
          live.stage = payload.stage ?? null;
          live.stageLabel = payload.label ?? null;
        } else if (name === "answer-v2") {
          // 🔴 RUN 5 · PART 1 — THE V2 ENVELOPE REPLACES `live` WHOLESALE, IT DOES NOT MERGE INTO IT.
          //
          // `live` is seeded with `sentences: []` and the old renderer's keys. Spreading v2 ON TOP
          // of that would leave a `sentences` key sitting under a v2 `kind`, and the whole
          // separation this run is built on is that the two shapes never coexist in one object —
          // see clarkAnswerV2.js's header on why v2 has no `sentences` key at all.
          //
          // `checks` is carried across because it is the TRACE, not the answer: it describes what
          // the server did and it is rendered by the shell, not by the answer component.
          live = { ...payload.answer, checks: live.checks, q: live.q ?? payload.answer?.q, scope: live.scope ?? payload.answer?.scope };
          // The envelope REPLACES `live` wholesale (see the note above), which correctly drops the
          // stage — the work it described is over. Written out rather than left implicit: a stage
          // label surviving onto a finished answer would say the pipeline is still running.
          live.stage = null; live.stageLabel = null;
        } else if (name === "authorities") {
          live.authorities = payload.authorities || [];
        } else if (name === "sentence") {
          live.sentences = [...live.sentences, payload.sentence];
        } else if (name === "notice") {
          // B6 — the unscoped disclosure. It arrives before the first sentence, so it is on
          // screen for the whole time the prose is being written rather than appended after it.
          live.persuasiveNotice = payload.notice;
        } else if (name === "bottomline") {
          // Last one wins, matching the server's `.pop()` — see the Steel stream route.
          live.bottomLine = payload.bottomLine;
        } else if (name === "done") {
          done = payload;
        } else if (name === "error") {
          errored = payload;
        }
        // 🔴 AFTER the frame has been dispatched, and for EVERY frame including the terminal
        // ones — a `done` that arrives at 119 s must not lose a race with a bound it satisfies.
        progressed(name);
        if (name !== "done" && name !== "error") onProgress({ ...live, checks: [...live.checks] });
      }
    }
  } catch {
    // 🔴 B6 STEP 4 — `painted` IS THE WHOLE POINT OF THIS RETURN. If the socket died after
    // verified sentences were already on screen, the caller must NOT discard the turn and fall
    // through to the citation path: that would delete gate-verified prose the user has read, which
    // is the retraction this arc forbids. If nothing had painted yet, the fallback is still right
    // — there is nothing to lose and an answer beats an apology (B1c's non-negotiable 3).
    clearTimers();
    // 🔴 LANE 7 · THE TERMINAL STATE. `streaming: false` is set HERE, on the live object the caller
    // patches back, because that flag is the ONLY thing standing between an aborted turn and a
    // permanent spinner. An abort that left it true would reproduce the exact defect this fix
    // exists to remove — and it would do so silently, since the turn still looks like it is working.
    live.streaming = false;
    // Distinct words per bound. "Timed out" and "connection dropped" are different events and a
    // user retrying deserves to know which one happened.
    const failure = abortReason ? FAILURES.RETRIEVAL_TIMEOUT : FAILURES.NETWORK;
    return { status: "error", failure, message: failure.message, painted: paintedCount(live), live, abortReason };
  }
  clearTimers();

  if (errored) {
    /* RUN GC1 — a server-sent `error` FRAME can carry a policy refusal too. Steel's public
       `/answer/stream` proves the shape exists (it emits the meter's block body as an error frame
       after flushing headers), so the same defence lives here even though main's route answers the
       plan gate as ordinary JSON before the stream opens: a refusal that arrives one frame late
       must still be a refusal, not an outage sentence. */
    const frameBlock = readMeterBlock(errored);
    if (frameBlock) {
      return { status: "capped", block: frameBlock,
        message: errored.message || frameBlock.serverMessage || FAILURES.QUOTA.message,
        data: errored, failure: FAILURES.QUOTA, painted: paintedCount(live), live };
    }
    const failure = classifyFailure({ status: errored.status, reason: errored.reason, policy: errored.error });
    return { status: "error", failure, message: errored.message || failure.message, painted: paintedCount(live), live };
  }
  // 🔴 A TRUNCATED STREAM IS AN ERROR, NOT A DECLINE. Rendering "I couldn't stand behind an
  // answer" because the socket died would put words in the gate's mouth.
  if (!done) {
    const failure = FAILURES.RETRIEVAL_TRUNCATED;
    return { status: "error", failure, message: failure.message, painted: paintedCount(live), live };
  }

  // `done` is authoritative and REPLACES the streamed set — see the Steel route's header on why
  // the two can differ and why the difference lands as one transition rather than a flicker.
  const data = { ...done, streaming: false };
  // 🔴 B6 · STEP 3 — an ERROR never renders as a decline. `done.error` is set only when synthesis
  // failed on our side (a parse or transport fault); the gate did not run, so the gate's words are
  // not ours to use. Belt and braces: the server already diverts these to the `error` event, and
  // this is the guard if a `done` ever carries one anyway.
  if (data.error?.kind) {
    const failure = classifyFailure({ status: data.error.status, reason: data.error.reason });
    return { status: "error", failure, message: data.message || failure.message };
  }
  if (!data.sentences?.length) return { status: "decline", data };
  return { status: "answer", data };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴🔴 RUN TA — THE REVEAL. WHY IT IS CLIENT-SIDE, OVER A `done` PAYLOAD, AND NOT A STREAM.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Adam: *"The first paint should then bring in other tokens but should not just jump to the end
 * when done."* Today an answer is silence, then a wall, all at once.
 *
 * The transport ALREADY streams — RUN PY's frame log shows `classified → check → stage →
 * heartbeats → done` — and everything a user can see mid-turn (the trace, the stage label, the
 * rail's authority cards) rides those frames. What does not stream is the prose: it arrives whole,
 * inside `done`, because `clark-answer.js:660` sends `...v2` and the v2 envelope is built in one
 * shot at the end of the pipeline.
 *
 * 🔴 AND IT MUST STAY THAT WAY. RUN RE established that the support gate runs AFTER composition:
 * `toV2Envelope` receives `support.prose` — prose whose unsupported quotations have already been
 * REMOVED and replaced by `{{unsupported:n}}` — and `blocking` is decided in the same pass. So
 * live-streaming model tokens would mean streaming text the gate has not yet ruled on, and the
 * only way to honour a later withhold is to take words off the screen. This arc forbids
 * retractions (B5/B6 spent two bundles removing one shape of it; `index.jsx`'s "painted > 0"
 * branch exists for another). Streaming the model would put it back.
 *
 * ⇒ The reveal is a RENDERING decision made after the verdict, not a transport change. Every
 * character it animates has already passed the gate. Zero server work, zero retraction risk, and
 * the failure mode of a bug in here is "the answer appears instantly", which is today's behaviour.
 *
 * ── ⚠️ A DECLARED DEVIATION FROM THE BRIEF'S NUMBER, WITH THE ARITHMETIC ─────────────────────────
 *
 * The brief asks for "~600–900 wpm effective **so** a cached answer completes in 1–2 s and a long
 * one in 3–5 s". Those two halves are not compatible on this product's actual answers, and the gap
 * is about sevenfold:
 *
 *     RUN SB measured the median Clark answer at 2,382 characters ≈ 400 words.
 *     400 words at 900 wpm = 26.7 s.  At 600 wpm = 40.0 s.
 *
 * A 27-second typewriter is the "crawl" the same sentence forbids, so the two constraints cannot
 * both be met and one of them has to give. The DURATION governs, for three reasons: it is what
 * Adam's ruling is actually about (an answer that "does not jump to the end" is a statement about
 * time, not about words per minute); it is stated twice and in 🔴; and it is the half the
 * verification section asks to be reported against. The wpm figure was written for answers an
 * order of magnitude shorter than the ones this route serves.
 *
 * So the pace is expressed as ONE constant in characters per second, and the effective wpm it
 * produces (~6,000) is REPORTED rather than hidden. At that rate the eye tracks the MOTION — text
 * filling the block, paragraph by paragraph — which is the thing Adam asked to see, and does not
 * track the words, which no reader does during a 4-second fill anyway.
 */

/* 🔴 THE PURE HALF LIVES IN `./answer/revealPace.mjs` AND IS RE-EXPORTED HERE — the same move, for
   the same reason, as `researchRoute.mjs` at the top of this file: this module is ESM in a
   commonjs package and node cannot import it, so anything that needs a test has to be `.mjs`.
   Timing code is the least reasonable-about code in the repo; it gets the testable file. */
export {
  REVEAL_CPS, REVEAL_MIN_MS, REVEAL_MAX_MS,
  revealWeight, revealTotal, revealDurationMs, revealSegments,
} from "./answer/revealPace.mjs";
